// src/components/CurrencyFormatter.tsx
import React, { useMemo } from 'react';
import { Tooltip } from '@mui/material';

export interface CurrencyFormatterProps {
  /** raw value (string or number) */
  value?: string | number | null;
  /** locale for formatting (default: en-US) */
  locale?: string;
  /** currency code (default: USD) */
  currency?: string;
  /** number of fraction digits (default: 2) */
  fractionDigits?: number;
}

const CurrencyFormatter: React.FC<CurrencyFormatterProps> = ({
  value,
  locale = 'en-US',
  currency = 'USD',
  fractionDigits = 2,
}) => {
  // Always call hooks at the top
  const formatter = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }),
    [locale, currency, fractionDigits]
  );

  // Handle empty or invalid values
  if (value == null || value === '') {
    return <>–</>;
  }

  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) {
    return <>–</>;
  }

  const formatted = formatter.format(num);
  return (
    <Tooltip title={formatted}>
      <span>{formatted}</span>
    </Tooltip>
  );
};

export default CurrencyFormatter;
